// src/pages/StaffManagement.jsx
import React from "react";
import { Plus, ChevronDown, HardHat, Pencil, Trash2, Eye, Phone, Mail, ToggleLeft, ToggleRight, Clock, RefreshCw, FileSpreadsheet } from "lucide-react";
import { clsx } from "clsx";
import { useStaffManagePage } from "@hooks";
import { StaffFormModal } from "./StaffFormModal";
import { StaffViewModal } from "./StaffViewModal";
import { ExcelUploadModal, PageHeader, FilterBar, DataTable } from "@components"; // 🌟 공통 컴포넌트 임포트

const DEPT_OPTIONS = ["서울위원", "경기위원"];

export function StaffManagement() {
  const { 
    filters, setFilters, page, onSearch, modal, setModal, 
    isExcelOpen, setIsExcelOpen, uploadExcel, isUploadingExcel, downloadTemplate,
    createStaff, updateStaff, deleteStaff, toggleUseYn, 
    isCreating, isUpdating, auth
  } = useStaffManagePage();

  const handleReset = () => {
    setFilters({
      deptNm: '전체', useYn: '전체', searchKeyword: '', pageNum: 1 // 페이지도 1페이지로 리셋
    });
  };

  const handleDelete = async (staffId) => {
    if (window.confirm("해당 요원을 미사용(퇴사) 처리하시겠습니까?")) await deleteStaff(staffId);
  };

  const handleToggle = async (staffId, currentUseYn) => {
    try { await toggleUseYn({ staffId, currentUseYn }); } 
    catch (e) { alert("상태 변경 실패"); }
  };

  // 상단 통계용 변수
  const totalActive = page.rows.filter((s) => s.useYn === "Y").length;
  const totalInactive = page.rows.filter((s) => s.useYn === "N").length;

  // 🌟 (추가) 수행요원 관리 초기 도움말
  const initialHelpText = `현장에 방문하여 컨설팅을 수행하는 '수행요원'의 인사 정보를 등록하고 관리하는 페이지입니다.

[주요 기능 안내]
1. 사용 여부 토글: 표 우측의 [사용/미사용] 버튼을 클릭하여 즉시 해당 요원의 배정 가능 상태를 변경할 수 있습니다. (퇴사자의 경우 미사용 처리)
2. 신규 등록 & 수정: [신규 등록] 또는 수정(✏️) 버튼을 통해 요원의 소속, 직급, 연락처 등을 관리합니다.
3. 엑셀 일괄 등록: [엑셀 업로드] 버튼을 통해 여러 요원을 한 번에 등록합니다. 기존 사번이 존재할 경우 해당 정보가 업데이트됩니다.

※ 참고: 이곳에 등록된 수행요원 목록이 '사업장 배정 관리' 화면의 담당자 선택 목록에 나타납니다.`;

  // 🌟 테이블 컬럼 정의
  const tableColumns = [
    { label: "사번/ID", align: "text-center" },
    { label: "성명", align: "text-center" },
    { label: "소속", align: "text-center" },
    { label: "직급/직책", align: "text-center" },
    { label: "연락처", align: "text-center" },
    { label: "이메일", align: "text-center" },
    { label: "등록일시", align: "text-center" },
    { label: "수정일시", align: "text-center" },
    { label: "사용 여부", align: "text-center" },
    { label: "관리", align: "text-center" }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* 1. Page Header */}
      <PageHeader 
        icon={HardHat} 
        iconBgColor="bg-orange-500"
        title="수행요원 관리" 
        description="현장 수행요원(tb_staff)의 정보를 등록·수정·조회합니다." 
        auth={auth} 
        helpId="staff_management"
        initialHelpText={initialHelpText}
      />

      {/* 🌟 통계 카드 (기존 구조 유지) */}
      {/* <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-6">
        {[
          { label: "전체 검색 결과", value: page.total,  color: "bg-slate-100  text-slate-700"  },
          { label: "현재 페이지 사용 중", value: totalActive,   color: "bg-green-100  text-green-700"  },
          { label: "현재 페이지 미사용", value: totalInactive, color: "bg-red-100    text-red-600"    },
        ].map(({ label, value, color }, idx) => (
          <div key={idx} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs text-slate-500 mb-1">{label}</p>
            <p className={clsx("inline-flex items-center rounded-lg px-2.5 py-1 text-2xl font-bold", color)}>{value}</p>
          </div>
        ))}
      </div> */}

      {/* 2. Filter Bar */}
      <FilterBar
        onReset={handleReset}
        actions={
          <>
            <button onClick={() => setModal({ mode: "add", data: null })} className="flex h-10 items-center gap-2 rounded-lg bg-orange-500 px-4 text-sm font-medium text-white hover:bg-orange-600 transition-colors">
              <Plus className="h-4 w-4" /> 신규 등록
            </button>
            <button onClick={() => setIsExcelOpen(true)} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4 text-green-600" /> 엑셀 업로드
            </button>
          </>
        }
      >
        <div className="space-y-1.5 w-36">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">소속</label>
          <div className="relative">
            <select value={filters.deptNm} onChange={(e) => { setFilters(p => ({...p, deptNm: e.target.value})); onSearch(); }} className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400">
              <option value="전체">전체</option>
              {DEPT_OPTIONS.map((d) => <option key={d}>{d}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-1.5 w-32">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">사용 여부</label>
          <div className="relative">
            <select value={filters.useYn} onChange={(e) => { setFilters(p => ({...p, useYn: e.target.value})); onSearch(); }} className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400">
              <option value="전체">전체</option>
              <option value="Y">사용(Y)</option>
              <option value="N">미사용(N)</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">사번 / 성명 / 연락처</label>
          <input type="text" value={filters.searchKeyword} onChange={(e) => setFilters(p => ({...p, searchKeyword: e.target.value}))} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="키워드 입력 후 Enter" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400" />
        </div>
      </FilterBar>

      {/* 3. Data Table */}
      <DataTable 
        columns={tableColumns}
        page={page}
        itemName="수행요원 데이터"
        onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
        emptyMessage="검색된 수행요원이 없습니다."
        renderRow={(s) => (
          <tr key={s.staffId} className="group hover:bg-slate-50/80 transition-colors text-center">
            <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{s.staffId}</td>
            <td className="px-4 py-3.5 font-semibold text-orange-600 cursor-pointer hover:underline" onClick={() => setModal({ mode: "view", data: s })}>{s.staffNm}</td>
            <td className="px-4 py-3.5 text-slate-600">{s.deptNm ?? "-"}</td>
            <td className="px-4 py-3.5">
              <span className={clsx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", s.positionNm === "팀장" ? "bg-blue-50 text-blue-700" : s.positionNm === "수습요원" ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700")}>
                {s.positionNm ?? "-"}
              </span>
            </td>
            <td className="px-4 py-3.5">
              {s.telNo ? <a href={`tel:${s.telNo}`} className="flex items-center justify-center gap-1.5 text-slate-600 hover:text-orange-600 transition-colors"><Phone className="h-3.5 w-3.5 text-slate-400" />{s.telNo}</a> : <span className="text-slate-300">-</span>}
            </td>
            <td className="px-4 py-3.5">
              {s.email ? <a href={`mailto:${s.email}`} className="flex items-center justify-center gap-1.5 text-slate-600 hover:text-orange-600 transition-colors"><Mail className="h-3.5 w-3.5 text-slate-400" />{s.email}</a> : <span className="text-slate-300">-</span>}
            </td>
            <td className="px-4 py-3.5 text-xs text-slate-400">
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.regDtm?.slice(0, 10) || '-'}</div>
                <div className="text-slate-300">{s.regDtm?.slice(11, 19) || ''}</div>
              </div>
            </td>
            <td className="px-4 py-3.5 text-xs text-slate-400">
              {s.modDtm ? (
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />{s.modDtm.slice(0, 10)}</div>
                  <div className="text-slate-300">{s.modDtm.slice(11, 19)}</div>
                </div>
              ) : <span className="text-slate-300">-</span>}
            </td>
            <td className="px-4 py-3.5">
              <button onClick={() => handleToggle(s.staffId, s.useYn)} title={`클릭하여 ${s.useYn === "Y" ? "미사용" : "사용"} 처리`} className={clsx("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all hover:opacity-80", s.useYn === "Y" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600")}>
                {s.useYn === "Y" ? <><ToggleRight className="h-3.5 w-3.5" />사용</> : <><ToggleLeft className="h-3.5 w-3.5" />미사용</>}
              </button>
            </td>
            <td className="px-4 py-3.5">
              <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setModal({ mode: "view", data: s })} title="상세 조회" className="p-1.5 rounded text-slate-400 hover:text-orange-600 hover:bg-orange-50"><Eye className="h-4 w-4" /></button>
                <button onClick={() => setModal({ mode: "edit", data: s })} title="수정" className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></button>
              </div>
            </td>
          </tr>
        )}
      />

      {/* Modals */}
      {modal.mode === "view" && <StaffViewModal staff={modal.data} onClose={() => setModal({ mode: null, data: null })} onEdit={() => setModal({ mode: "edit", data: modal.data })} />}
      {(modal.mode === "add" || modal.mode === "edit") && (
        <StaffFormModal type={modal.mode} staff={modal.data} onClose={() => setModal({ mode: null, data: null })} onSubmit={modal.mode === "add" ? createStaff : updateStaff} isSubmitting={modal.mode === "add" ? isCreating : isUpdating} />
      )}
      <ExcelUploadModal 
        isOpen={isExcelOpen} 
        onClose={() => setIsExcelOpen(false)} 
        onUpload={(file) => uploadExcel(file).then(() => alert('엑셀 업로드가 완료되었습니다.')).catch(e => alert('업로드 실패: ' + e.message))} 
        isUploading={isUploadingExcel} 
        title="수행요원 엑셀 일괄 등록" 
        description="사번, 성명, 소속 부서 등이 포함된 수행요원 목록을 엑셀로 일괄 업로드합니다. 중복된 사번은 덮어쓰기 됩니다." 
        onDownloadTemplate={downloadTemplate} 
      />
    </div>
  );
}